import Configuration from '@/agilis/util/Configuration'
import { mapActions, mapGetters } from 'vuex'
import jsonLogic from '@/plugins/jsonLogic'
import Vue from 'vue'

const MIN_COLLABORATORS_FOR_DISCUSSION = 2

function createQueue (tasks, maxNumOfWorkers = 4) {
  let numOfWorkers = 0
  let taskIndex = 0

  // eslint-disable-next-line promise/param-names
  return new Promise(done => {
    const handleResult = index => result => {
      tasks[index] = result
      numOfWorkers--
      getNextTask()
    }
    const getNextTask = () => {
      if (numOfWorkers < maxNumOfWorkers && taskIndex < tasks.length) {
        tasks[taskIndex]().then(handleResult(taskIndex)).catch(handleResult(taskIndex))
        taskIndex++
        numOfWorkers++
        getNextTask()
      } else if (numOfWorkers === 0 && taskIndex === tasks.length) {
        done(tasks)
      }
    }
    getNextTask()
  })
}

export default {
  name: 'TrackerDataTableItemCollaborators',

  props: {
    trackerDTO: Object
  },

  data () {
    return {
      dialog: false,
      createDiscussionDialog: false,
      collaboratorArray: [],
      userArray: [],
      selected: [],
      internalSearch: '',
      discussion: {
        discussionParticipants: [],
        welcomeMessage: ''
      }
    }
  },

  created () {
    this.fetchUsers()
    this.fetchCollaborators()
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      trackerType: 'contextStore/trackerType',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading'
    }),

    title () {
      return this.$t('label.collaboratorPopupTitle')
    },

    filteredUserArray () {
      const userIdList = this.collaboratorArray.map(c => c.trackerUser.id)
      this.userArray.filter(p => !userIdList.includes(p.id))
      this.userArray.forEach(element => {
        element.fullName = this.computeFullName(element)
      })
      return this.userArray
    },

    userCanAddCollaborator () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'collaborator', 'post', this.trackerDTO, this.userId)
    },

    userCanRemoveCollaborator () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'collaborator', 'delete', this.trackerDTO, this.userId)
    },

    userCanCreateDiscussion () {
      return Configuration.auth.userCanAccessFeatureInclude(this.selectedContext, 'createDiscussion')
    },

    minCollaboratorsForDiscussion () {
      return MIN_COLLABORATORS_FOR_DISCUSSION
    }
  },

  watch: {
    selected () {
      this.internalSearch = ''
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),
    label (elem) {
      return `${elem.lastName} ${elem.firstName}`
    },

    fetchUsers () {
      const url = Vue.axios.agilisUrlTrackerCollaboratorsOverride(
        this.applicationId,
        this.trackerTypeId)
      Vue.axios
        .get(
          url,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.userArray = response.data
        })
        .catch((error) => {
          this.notifyError(error)
        })
    },

    fetchCollaborators () {
      this.startLoader({ id: 'tracker-collaborators' })
      const url = Vue.axios.agilisUrlTrackerCollaborators(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      Vue.axios
        .get(
          url,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.collaboratorArray = response.data
          this.selected = this.getCollaboratorWithFullName(response.data)
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'tracker-collaborators' })
        })
    },

    saveCollaborator () {
      this.startLoader({ id: 'save-tracker-collaborators' })
      const url = Vue.axios.agilisUrlTrackerCollaborators(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)

      const added = this.selected.filter(p => !this.userExists(p.id))
      const removed = this.collaboratorArray.filter(c => !this.selected.find(p => p.id === c.trackerUser.id))

      const tasks = [
        // 1 post for each collaborator selected
        ...added.map(p => {
          return () => Vue.axios.post(
            url,
            this.getBackendObjFromPerson(p),
            Vue.axios.agilisRequestConfig(url)
          )
        }),

        // 1 post for each collaborator deselected
        ...removed.map(c => {
          const url = Vue.axios.agilisUrlTrackerCollaboratorsDelete(
            this.applicationId,
            this.trackerTypeId,
            this.trackerId,
            c.id)
          return () => Vue.axios.delete(
            url,
            {
              ...Vue.axios.agilisRequestConfig(url),
              data: c
            })
        })
      ]
      createQueue(tasks, 1)
        .then(responses => {
          this.dialog = false
          this.fetchCollaborators()
          this.$emit('componentUpdated')
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'save-tracker-collaborators' })
        })
    },

    getBackendObjFromPerson (person) {
      return {
        'tracker': {
          'applicationId': this.applicationId,
          'id': this.trackerId,
          'trackerTypeId': this.trackerTypeId,
          'workspaceId': this.workspaceId
        },
        'trackerUser': {
          'id': person.id
        }
      }
    },

    deleteCollaborator (c) {
      this.startLoader({ id: 'delete-tracker-collaborator', value: c.id })
      const url = Vue.axios.agilisUrlTrackerCollaboratorsDelete(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        c.id)
      Vue.axios
        .delete(
          url,
          {
            ...Vue.axios.agilisRequestConfig(url),
            data: c
          })
        .then((response) => {
          this.collaboratorArray = this.collaboratorArray.filter(elem => elem.id !== c.id)
          this.selected = this.selected.filter(elem => elem.id !== c.trackerUser.id)
          this.$emit('componentUpdated')
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'delete-tracker-collaborator' })
        })
    },

    userExists (userId) {
      return this.collaboratorArray.some(user => {
        return user.trackerUser.id === userId
      })
    },

    getCollaboratorWithFullName (collaborators) {
      let collaboratorArray = []
      collaborators.forEach(element => {
        element.trackerUser.fullName = this.computeFullName(element.trackerUser)
        collaboratorArray.push(element.trackerUser)
      })
      return collaboratorArray
    },

    computeFullName (userDTO) {
      return userDTO.lastName + ' ' + userDTO.firstName
    },

    closeDialog () {
      this.selected = this.getCollaboratorWithFullName(this.collaboratorArray)
      this.dialog = false
    },

    closeDiscussionDialog () {
      this.discussion.discussionParticipants = []
      this.discussion.welcomeMessage = ''
      this.createDiscussionDialog = false
    },

    resetOnBlur () {
      setTimeout(() => {
        this.internalSearch = ''
      }, 200)
    },

    async createDiscussionWithUser () {
      this.startLoader({ id: 'create-discussion' })
      const url = this.$axios.agilisUrlRocketChat()
      try {
        this.discussion.channelName = this.trackerType.chatChannelName
        this.discussion.discussionName = jsonLogic.apply(this.trackerType.chatDiscussionName.jsonLogic, this.trackerDTO)
        const response = await this.$axios.post(
          url,
          this.discussion,
          this.$axios.agilisRequestConfig(url)
        )
        window.open(this.$envmap.VUE_APP_AGILIS_ROCKETCHAT_ENDPOINT + response.data.discussion.name, 'rocketchat').focus()
      } catch (error) {
        this.notifyError(error)
      }
      this.closeDiscussionDialog()
      this.stopLoader({ id: 'create-discussion' })
    }
  }
}
